import { cloneDeep } from 'lodash'
import qs from 'qs'

// 50 é o max por página do core. O catálogo tem ~31 tipos e o front filtra client-side (busca,
// GrandSlam, No-Gi, Curso); com authoring o 51º desapareceria em silêncio da tabela e do índice
// de reúso. Então pagina até esgotar, como o catálogo de moldes.
const PAGE_SIZE = 50
const MAX_PAGES = 20

const defaultState = {
  loading: false,
  current: null,
  paginatedList: {
    list: [],
    pageCount: 0,
    totalCount: null
  },
  // Qual filtro de status a lista em cache representa. Quem depende de ver TODOS os tipos (o
  // contador de reúso: um tipo desativado que aponta o molde também conta) precisa saber se o que
  // está em cache veio filtrado, senão conta menos e o aviso mente. Toda escrita zera: a lista
  // em cache deixou de ser a do core.
  loadedStatus: null
}

export const state = () => cloneDeep(defaultState)

// O `reuseCount` de cada molde conta os tipos que o apontam: criar, re-apontar ou excluir tipo o
// envelhece. Desativar não, porque tipo desativado continua apontador. Recarrega (e não só limpa)
// porque o form que fica aberto lê o catálogo, e a falha não desfaz a escrita do tipo.
const refreshMolds = dispatch =>
  dispatch('championshipMolds/refresh', null, { root: true }).catch(() => {})

export const getters = {
  loading(state) {
    return state.loading
  },
  current(state) {
    return state.current
  },
  paginatedList(state) {
    return state.paginatedList
  },
  loadedStatus(state) {
    return state.loadedStatus
  }
}

export const mutations = {
  loading(state, loading) {
    state.loading = loading
  },
  current(state, current) {
    state.current = current
  },
  list(state, list) {
    state.paginatedList.list = list
  },
  loadedStatus(state, status) {
    state.loadedStatus = status
  },
  totalCount(state, totalCount) {
    state.paginatedList.totalCount = totalCount
  },
  pageCount(state, pageCount) {
    state.paginatedList.pageCount = pageCount
  }
}

export const actions = {
  // `status` default do core é 'active': tipo desativado sai do authoring, mas segue consultável
  // pela aba de desativados.
  async search({ commit }, { status = 'active' } = {}) {
    commit('loading', true)

    try {
      const list = []
      let page = 1
      let meta = { recordCount: 0, pageCount: 0 }

      // Para quando a página vem incompleta: é o fim do catálogo. Guarda em `page` para um core
      // que ignorasse a paginação não virar laço infinito.
      while (page <= MAX_PAGES) {
        const response = await this.$coreApi.get('staff/championship_types', {
          params: {
            filter: { status },
            page: { number: page, size: PAGE_SIZE }
          },
          paramsSerializer: params => qs.stringify(params)
        })

        list.push(...response.data.data)
        meta = response.data.meta
        page += 1

        if (response.data.data.length < PAGE_SIZE) break
      }

      commit('list', list)
      commit('loadedStatus', status)
      commit('totalCount', meta.recordCount)
      commit('pageCount', meta.pageCount)
    } finally {
      commit('loading', false)
    }
  },
  async getById({ commit }, id) {
    const response = await this.$coreApi.$get(`staff/championship_types/${id}`)

    commit('current', response.data)
  },
  // Devolve os avisos não-bloqueantes do core (apontador sem molde, etiqueta reusada entre kinds)
  // para a tela mostrar sem uma segunda chamada.
  async create({ commit, dispatch }, payload) {
    const response = await this.$coreApi.post('staff/championship_types', payload)
    commit('loadedStatus', null)
    await refreshMolds(dispatch)

    return response.data
  },
  // O `name` NÃO vai no payload: o core bloqueia rename porque o nome é chave natural viva
  // cross-system. A tela mostra o campo travado.
  async update({ commit, dispatch }, { id, ...payload }) {
    const response = await this.$coreApi.post(`staff/championship_types/${id}/update`, payload)
    commit('loadedStatus', null)
    await refreshMolds(dispatch)

    return response.data
  },
  async deactivate({ commit }, id) {
    await this.$coreApi.post(`staff/championship_types/${id}/deactivate`, {})
    commit('loadedStatus', null)
  },
  async reactivate({ commit }, id) {
    await this.$coreApi.post(`staff/championship_types/${id}/reactivate`, {})
    commit('loadedStatus', null)
  },
  // Só passa para tipo nunca referenciado; com campeonato vinculado o core exige desativação.
  async destroy({ commit, dispatch }, id) {
    await this.$coreApi.delete(`staff/championship_types/${id}`)
    commit('loadedStatus', null)
    await refreshMolds(dispatch)
  },
  // Piso de pontuação mínima de ranking: por tipo E por grupo. O mesmo molde é apontado por tipos
  // com pisos diferentes, e um tipo pode exigir valores diferentes por faixa — daí as duas chaves.
  // Valor nulo remove o gate.
  async setMinRanking(_context, { id, moldOptionId, value }) {
    await this.$coreApi.post(`staff/championship_types/${id}/min_ranking`, {
      championship_mold_option_id: moldOptionId,
      min_ranking_pontuation: value === '' ? null : value
    })
  }
}
